<!--
  This is the example list which contains all the elements we should need
  - Search
  - SortOrder
  - Filters
  - Item

  See content-filter.vue or content-library.vue for example on how to create a new list template
-->

<template>
  <div class="list-example">
    <Search :store="store" />
    <Filtr :store="store" name="SortOrder" label="Sort By" />
    <Filters :store="store" />
    <ol class="list-example__items">
      <li class="list-example__item" v-for="item in items" :key="item.Id">
        <slot :item="item">
          <h2>{{item.Title}}</h2>
          <p>{{item.Description}}</p>
        </slot>
      </li>
    </ol>
    <button
      class="btn btn--full-width"
      v-if="itemsRemaining > 0"
      data-tsat="t06-load_more"
      @click="loadMore"
    >{{loadMoreButtonText}}</button>
  </div>
</template>

<script>
import Vuex from 'vuex';
import Filters from './Filters.vue';
import Search from './Search.vue';
import Filtr from './Filtr.vue';
import reduce from 'lodash/reduce';
import Loading from '@/vue/components/Form/Loading.vue';

export default {
  name: 'ResultsList',

  components: {
    Filters,
    Search,
    Filtr,
    Loading
  },

  props: {
    // The vuex store we use for fetching data
    store: {
      type: String,
      default: 'items'
    },

    // parent page id (sent in api requests if set)
    parentId: {
      type: Number,
      default: null
    },

    // The amount of results we show on load
    startAmount: {
      type: Number,
      default: 2
    },

    // the amount of extra results we display on load more
    loadMoreAmount: {
      type: Number,
      default: 2
    },

    // the button text
    loadMoreButtonText: {
      type: String,
      default: 'Load More'
    },

    // whether we are doing a tag search
    tagSearch: {
      type: Boolean,
      default: false
    },

    // used on main search results page to weed out results that have been turned off in the back end
    isSiteSearch: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // mapped state from the store
    ...Vuex.mapState({
      items(state) {
        const storeState = state[this.store];
        if (storeState.items && storeState.items.length > 0) {
          GTM.viewItemList(storeState);
        }
        return storeState.items;
      },
      itemsRemaining(state) {
        return state[this.store].itemsRemaining;
      },
      itemsCount(state) {
        return state[this.store].itemsCount;
      },
      Take(state) {
        return state[this.store].Take;
      },
      SearchTerm(state) {
        return state[this.store].SearchTerm;
      },
      Tag(state) {
        return state[this.store].Tag;
      },
      CurrentSearch(state) {
        return state[this.store].CurrentSearch;
      },
      showNoResultsContent(state) {
        return state[this.store].showNoResultsContent;
      },
      currentPage(state) {
        return state[this.store].currentPage;
      },
      totalPages(state) {
        return state[this.store].totalPages;
      },
      loading(state) {
        return state[this.store].loading;
      }
    }),

    // mapped getter from the store
    urlData() {
      return this.$store.getters[`${this.store}/urlData`];
    }
  },

  methods: {
    // bind events
    bindEvents() {
      // update the store/items when the url changes
      window.onpopstate = e => {
        this.applyRouterData();
        this.$store.dispatch(`${this.store}/fetchItems`, items => {
          if (items && items.length > 0) {
            GTM.viewItemList(items);
          }
        });
      };
    },

    // used on people list to get new results when entering a search term
    updateSearch() {
      this.$store.dispatch(`${this.store}/fetchItems`, items => {
        if (items && items.length > 0) {
          GTM.viewItemList(items);
        }
      });
    },

    // load more button clicked
    loadMore() {
      this.$store.commit(
        `${this.store}/setTake`,
        this.Take + this.loadMoreAmount
      );

      // get next results
      this.$store.dispatch(`${this.store}/fetchItems`, items => {
        if (items && items.length > 0) {
          GTM.viewItemList(items);
        }
      });
    },

    // apply data from vue router
    applyRouterData() {
      // we have to clone the router object otherwise router.push fails later on
      // https://github.com/vuejs/vue-router/issues/1182#issuecomment-405977211
      const currentRouteQuery = JSON.parse(
        JSON.stringify(this.$router.currentRoute.query)
      );

      // get any missing params
      // https://stackoverflow.com/a/31686152/3684315
      const missingUrlParams = reduce(
        this.urlData,
        (result, value, key) => {
          return typeof currentRouteQuery[key] !== 'undefined'
            ? result
            : result.concat(key);
        },
        []
      );

      // add missing url params to the array so they are removed if necessary
      missingUrlParams.forEach(attr => {
        currentRouteQuery[attr] = '';
      });

      // loop params and call their mutation
      for (let attr in currentRouteQuery) {
        const mutationName = `${this.store}/set${attr}`;

        // if the mutation doesnt exist
        // fallback to a global mutation for query params
        if (!this.$store._mutations[mutationName]) {
          this.$store.commit(`${this.store}/setQueryParam`, {
            attribute: attr,
            value: currentRouteQuery[attr]
          });
          console.warn(
            '[vuex] Mutation does not exist (using fallback setQueryParam instead):',
            mutationName
          );
        } else {
          this.$store.commit(mutationName, currentRouteQuery[attr]);
        }
      }
    }
  },

  created() {
    this.$store.commit(
      `${this.store}/setChildItemsOnly`,
      this.showChildItemsOnly
    );

    this.$store.commit(`${this.store}/setIsSiteSearch`, this.isSiteSearch);

    // set page id on store
    this.$store.commit(`${this.store}/setParentId`, this.parentId);

    // set how many items we load
    this.$store.commit(`${this.store}/setInitialTake`, this.startAmount);
    this.$store.commit(`${this.store}/setTake`, this.startAmount);

    // apply store data from router
    this.applyRouterData();

    // setup events
    this.bindEvents();

    // load results, filters and sort options
    this.$store.dispatch(`${this.store}/fetchItems`, true);
    this.$store.dispatch(`${this.store}/fetchSortOrderOptions`);
  }
};
</script>
